import React, {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useRef,
} from "react";

const WidgetHeightContext = createContext(null);

export const WidgetHeightProvider = ({ children, onHeightChange, disabled }) => {
  const rootRef    = useRef(null);
  const openCount  = useRef(0);

  const calculateHeight = () => {
    if (!rootRef.current) return 0;
    return Math.ceil(rootRef.current.getBoundingClientRect().bottom + window.scrollY) + 20;
  };

  const setNaturalHeight = useCallback(() => {
    if (disabled || !onHeightChange) return;
    onHeightChange(`${calculateHeight()}px`);
  }, [disabled, onHeightChange]);

  // Call when any popover opens — instantly expand to 100vh
  const onPopoverOpen = useCallback(() => {
    if (disabled || !onHeightChange) return;
    openCount.current += 1;
    onHeightChange("100vh");
  }, [disabled, onHeightChange]);

  // Call when any popover closes — collapse only when ALL are closed
  const onPopoverClose = useCallback(() => {
    if (disabled || !onHeightChange) return;
    openCount.current = Math.max(0, openCount.current - 1);
    if (openCount.current === 0) setNaturalHeight();
  }, [disabled, onHeightChange, setNaturalHeight]);

  // Initial height before first paint
  useLayoutEffect(() => {
    if (disabled || !onHeightChange || !rootRef.current) return;
    setNaturalHeight();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resize observer — only updates height when no popover is open
  React.useEffect(() => {
    if (disabled || !rootRef.current) return;
    const ro = new ResizeObserver(() => {
      if (openCount.current === 0) setNaturalHeight();
    });
    ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, [disabled, setNaturalHeight]);

  return (
    <WidgetHeightContext.Provider value={{ onPopoverOpen, onPopoverClose, rootRef }}>
      {children}
    </WidgetHeightContext.Provider>
  );
};

export const useWidgetHeight = () => {
  const ctx = useContext(WidgetHeightContext);
  return {
    onPopoverOpen:  ctx?.onPopoverOpen  ?? (() => {}),
    onPopoverClose: ctx?.onPopoverClose ?? (() => {}),
    rootRef:        ctx?.rootRef        ?? { current: null },
  };
};